import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Stack,
} from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import AppDocuServiceCall from './AppDocu/_Utils/AppDocuServiceCall';
import { notifySomething } from './AppDocu/_Utils/AppDocuCommon';
import LoadingSpinner from '../comp/gen/LoadingSpinner';
import UserAuthRequired from '../comp/gen/UserAuthRequired';

const AppDocuActivityStatsPage = (props) => {
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userState, setUserState] = useState('init');
  const [stats, setStats] = useState(null);
  const [filterTech, setFilterTech] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const res = await AppDocuServiceCall.checkUser();
      if (res.data && res.data.is_valid === false) {
        setUserState('notAllowed');
      } else {
        setUserState('valid');
      }
    } catch (error) {
      setUserState('error');
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (month && month > 0) {
        params.month = month;
      }

      const res = await AppDocuServiceCall.getActivityStats(params);
      
      if (res.data && res.data.is_valid === false) {
        notifySomething(res.data.message || 'Error fetching statistics', 'error');
        setStats(null);
      } else {
        setStats(res.data);
        notifySomething('Statistics loaded successfully', 'success');
      }
    } catch (error) {
      notifySomething('Error fetching statistics', 'error');
      console.error(error);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!year) {
      notifySomething('Please enter a year', 'warning');
      return;
    }
    fetchStats();
  };

  // Calcola statistiche aggregate
  const getAggregatedStats = () => {
    if (!stats || !stats.stats_by_tech) return null;

    const techStats = {};
    const activityTypes = new Set();
    const overallByType = {};
    const customerStats = {};

    stats.stats_by_tech.forEach(item => {
      const tech = item.tech_username;
      const activityType = item.activity_type || 'none';
      
      activityTypes.add(activityType);

      if (!techStats[tech]) {
        techStats[tech] = {
          total: 0,
          byType: {},
        };
      }

      techStats[tech].total += item.count;
      techStats[tech].byType[activityType] = (techStats[tech].byType[activityType] || 0) + item.count;
      overallByType[activityType] = (overallByType[activityType] || 0) + item.count;
    });

    // Aggrega per customer usando stats_by_customer
    const customerSource = stats.stats_by_customer || [];
    customerSource.forEach(item => {
      const customer = item.customer_username;
      if (!customerStats[customer]) {
        customerStats[customer] = { count: 0, totalMinutes: 0 };
      }
      customerStats[customer].count += parseInt(item.count, 10) || 0;
      customerStats[customer].totalMinutes += parseInt(item.total_minutes, 10) || 0;
    });

    return {
      techStats,
      activityTypes: Array.from(activityTypes).sort(),
      overallByType,
      customerStats,
    };
  };

  // Formatta i minuti come ore:minuti (es. 1h 05m)
  const formatMinutes = (totalMinutes) => {
    if (!totalMinutes) return '—';
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hours}h ${String(mins).padStart(2, '0')}m`;
  };

  // Genera colori per i grafici
  const getActivityTypeColor = (type) => {
    const colors = {
      'Assistance': '#FF6384',
      'Development': '#36A2EB',
      'Handbook': '#FFCE56',
      'Meetings': '#4BC0C0',
      'Training': '#9966FF',
      'Quality Issue': '#FF9F40',
      'none': '#CCCCCC',
    };
    return colors[type] || '#' + Math.floor(Math.random()*16777215).toString(16);
  };

  // Crea opzioni per grafico a torta complessivo
  const getOverallPieOptions = () => {
    if (!aggregated) return null;

    const totalActivities = Object.values(aggregated.overallByType).reduce((sum, count) => sum + count, 0);
    
    const data = Object.entries(aggregated.overallByType)
      .map(([type, count]) => ({
        name: type === 'none' ? '<i>none</i>' : type,
        y: count,
        color: getActivityTypeColor(type),
      }));

    return {
      chart: { type: 'pie', height: 400 },
      title: { text: `Overall Activity Types Distribution - Total: ${totalActivities}` },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)'
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%',
            useHTML: true,
          },
        },
      },
      series: [{ name: 'Activities', data }],
    };
  };

  // Crea opzioni per grafico a torta per singolo tecnico
  const getTechnicianPieOptions = (tech, data) => {
    const pieData = Object.entries(data.byType)
      .map(([type, count]) => ({
        name: type === 'none' ? '<i>none</i>' : type,
        y: count,
        color: getActivityTypeColor(type),
      }));

    return {
      chart: { type: 'pie', height: 300 },
      title: { text: `${tech} - Activity Types (Total: ${data.total})` },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)'
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%',
            useHTML: true,
          },
        },
      },
      series: [{ name: 'Activities', data: pieData }],
    };
  };

  if (userState === 'init') return <LoadingSpinner />;
  if (userState === 'notAllowed') return <UserAuthRequired notallowed />;
  if (userState === 'error') return <UserAuthRequired notlogged />;

  const aggregated = getAggregatedStats();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Activity Statistics
      </Typography>

      {/* Form di ricerca */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Year"
              type="number"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value) || currentYear)}
              variant="outlined"
              inputProps={{ min: 2020, max: 2030 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Month (Optional)</InputLabel>
              <Select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                label="Month (Optional)"
              >
                <MenuItem value={0}>All Year</MenuItem>
                <MenuItem value={1}>January</MenuItem>
                <MenuItem value={2}>February</MenuItem>
                <MenuItem value={3}>March</MenuItem>
                <MenuItem value={4}>April</MenuItem>
                <MenuItem value={5}>May</MenuItem>
                <MenuItem value={6}>June</MenuItem>
                <MenuItem value={7}>July</MenuItem>
                <MenuItem value={8}>August</MenuItem>
                <MenuItem value={9}>September</MenuItem>
                <MenuItem value={10}>October</MenuItem>
                <MenuItem value={11}>November</MenuItem>
                <MenuItem value={12}>December</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading}
              sx={{ height: '56px' }}
            >
              {loading ? <CircularProgress size={24} /> : 'Search'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Risultati */}
      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {stats && !loading && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '140px' }}>
                  <Typography color="white" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Total Activities
                  </Typography>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>{stats.count || 0}</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    {month ? `${year}/${month}` : year}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '140px' }}>
                  <Typography color="white" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Total Technicians
                  </Typography>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {aggregated ? Object.keys(aggregated.techStats).length : 0}
                  </Typography>
                  <Box sx={{ height: '20px' }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '140px' }}>
                  <Typography color="white" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Activity Types
                  </Typography>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {aggregated ? aggregated.activityTypes.length : 0}
                  </Typography>
                  <Box sx={{ height: '20px' }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '140px' }}>
                  <Typography color="white" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Period
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {month ? `${year}/${String(month).padStart(2, '0')}` : `Year ${year}`}
                  </Typography>
                  <Box sx={{ height: '20px' }} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Activity Types Legend */}
          {aggregated && aggregated.activityTypes.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Activity Types
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {aggregated.activityTypes.map((type) => (
                  <Chip 
                    key={type} 
                    label={type === 'none' ? <em>none</em> : type} 
                    size="small" 
                    sx={{ 
                      m: 0.5,
                      backgroundColor: getActivityTypeColor(type),
                      color: 'white',
                      fontWeight: 'bold',
                    }} 
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Grafico a torta complessivo */}
          {aggregated && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <HighchartsReact highcharts={Highcharts} options={getOverallPieOptions()} />
            </Paper>
          )}

          {/* Statistiche per Tecnico */}
          {aggregated && (
            <Paper sx={{ mb: 3 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Statistics by Technician
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell>
                        <strong>Technician</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Total</strong>
                      </TableCell>
                      {aggregated.activityTypes.map((type) => (
                        <TableCell key={type} align="right">
                          <strong>{type === 'none' ? <em>none</em> : type}</strong>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(aggregated.techStats)
                      .sort((a, b) => b[1].total - a[1].total)
                      .map(([tech, data]) => (
                        <TableRow key={tech} hover>
                          <TableCell sx={{ fontWeight: 'bold', color: '#1976d2' }}>{tech}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                            {data.total}
                          </TableCell>
                          {aggregated.activityTypes.map((type) => (
                            <TableCell key={type} align="right">
                              {data.byType[type] || 0}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Grafici a torta per ogni tecnico */}
          {aggregated && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Activity Distribution by Technician
              </Typography>
              <Grid container spacing={3}>
                {Object.entries(aggregated.techStats)
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([tech, data]) => (
                    <Grid item xs={12} sm={6} md={4} key={tech}>
                      <Paper sx={{ p: 2 }}>
                        <HighchartsReact highcharts={Highcharts} options={getTechnicianPieOptions(tech, data)} />
                      </Paper>
                    </Grid>
                  ))}
              </Grid>
            </Box>
          )}

          {/* Tabella statistiche per Customer */}
          {aggregated && aggregated.customerStats && (
            <Paper sx={{ mb: 3 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Statistics by Customer
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell>
                        <strong>Customer</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Total Activities</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Effort</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(aggregated.customerStats)
                      .sort((a, b) => b[1].count - a[1].count)
                      .map(([customer, data], index) => (
                        <TableRow key={customer} hover sx={{ backgroundColor: index % 2 === 0 ? 'rgba(240, 248, 255, 0.5)' : 'white' }}>
                          <TableCell sx={{ fontWeight: 'bold', color: '#1976d2' }}>{customer}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>{data.count}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>{formatMinutes(data.totalMinutes)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Tabella dettagliata (tutti i record) */}
          {stats.stats_by_tech && stats.stats_by_tech.length > 0 && (() => {
            // Ordina i dati
            const sortedData = [...stats.stats_by_tech].sort((a, b) => {
              // Prima ordina per technician
              if (a.tech_username !== b.tech_username) {
                return a.tech_username.localeCompare(b.tech_username);
              }
              // Poi ordina per count decrescente
              return b.count - a.count;
            });

            // Applica filtri
            const filteredData = sortedData.filter(item => {
              const matchTech = !filterTech || item.tech_username.toLowerCase().includes(filterTech.toLowerCase());
              const matchCustomer = !filterCustomer || item.customer_username.toLowerCase().includes(filterCustomer.toLowerCase());
              return matchTech && matchCustomer;
            });

            // Crea una mappa dei tecnici unici per assegnare i colori
            const uniqueTechs = [];
            sortedData.forEach(item => {
              if (!uniqueTechs.includes(item.tech_username)) {
                uniqueTechs.push(item.tech_username);
              }
            });

            // Crea una mappa tecnico -> colore
            const techColorMap = {};
            uniqueTechs.forEach((tech, idx) => {
              techColorMap[tech] = idx % 2 === 0 ? '#e3f2fd' : '#fff3e0';
            });

            return (
              <Paper>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Detailed Statistics ({filteredData.length} of {stats.stats_by_tech.length} records)
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Filter by Technician"
                        value={filterTech}
                        onChange={(e) => setFilterTech(e.target.value)}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Filter by Customer"
                        value={filterCustomer}
                        onChange={(e) => setFilterCustomer(e.target.value)}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Box>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell>
                          <strong>Technician</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Customer</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Activity Type</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Count</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData.map((item, index) => {
                        const prevItem = index > 0 ? filteredData[index - 1] : null;
                        const techChanged = prevItem && prevItem.tech_username !== item.tech_username;
                        const activityType = item.activity_type || 'none';
                        const bgColor = techColorMap[item.tech_username];
                        
                        return (
                          <TableRow 
                            key={index}
                            sx={{
                              backgroundColor: bgColor,
                              borderTop: techChanged ? '3px solid #1976d2' : 'none',
                              '&:hover': {
                                opacity: 0.8,
                              }
                            }}
                          >
                            <TableCell>
                              {item.tech_username}
                            </TableCell>
                            <TableCell>{item.customer_username}</TableCell>
                            <TableCell>
                              <Chip 
                                label={activityType === 'none' ? <em>none</em> : activityType} 
                                size="small"
                                sx={{
                                  backgroundColor: getActivityTypeColor(activityType),
                                  color: 'white',
                                  fontWeight: 'bold',
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{item.count}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })()}
        </>
      )}

      {!loading && !stats && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            Select year and month, then click Search to load statistics
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AppDocuActivityStatsPage;
